import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown, Mail, Shield, FileText, Send } from "lucide-react";

const SUPPORT_EMAIL = "hello@sayitbetter.me";
const APP_VERSION = "1.0.0";

const FAQS = [
  {
    q: "What is SayItBetter?",
    a: "SayItBetter is your private AI message coach. Paste a text you're about to send and we'll analyze the tone, show you a risk score, and suggest calmer rewrites — so you don't send something you'll regret.",
  },
  {
    q: "Is SayItBetter free to use?",
    a: "Yes. You get 3 free message checks every day, every day, forever. PRO members get unlimited checks plus advanced features like Conflict Score tracking, received-message context, and early access to new features. PRO is $2.99/month or — for a limited pre-launch offer — $5.99 for a full year.",
  },
  {
    q: "How does the risk score work?",
    a: "Our AI analyzes your message for tone, accusations, emotional intensity, and common communication pitfalls. It returns a 0-10 risk score and a traffic-light level (green/yellow/red) with a short explanation, plus 1-3 rewritten versions in different tones.",
  },
  {
    q: "Do you store or read my messages?",
    a: "If you're logged in, your analyses are saved privately to your account so you can review them in the History tab. We never share your messages with third parties, never sell data, and never use your messages to train public AI models. You can delete your entire history anytime from Settings → Clear history, or delete your account entirely from Settings → Delete account.",
  },
  {
    q: "Can I use SayItBetter without creating an account?",
    a: "Yes! Tap \"Try without an account\" on the welcome screen. Guest users get full access to the message analyzer with the 3-per-day free limit. Your analyses won't be saved to a history — create a free account anytime to unlock that.",
  },
  {
    q: "How do I subscribe to PRO?",
    a: "We're currently in pre-launch. Tap any \"Reserve\" button to join the waitlist at the special $5.99/year price. You won't be charged anything today — when we launch on Google Play and the Apple App Store, we'll email you a private promo code to activate your subscription.",
  },
  {
    q: "When will the app be available on Google Play or the App Store?",
    a: "We're finalizing store submissions now. Reserve the $5.99/year deal to be the first to hear when it goes live — we'll email you the launch link and your promo code the moment it's approved.",
  },
  {
    q: "Will I be charged today if I reserve the $5.99/year deal?",
    a: "No. Reserving captures your email so we can send you a one-time promo code at launch. Payment only happens inside the official Google Play or App Store, through their secure billing systems.",
  },
  {
    q: "Can I cancel my subscription?",
    a: "Yes. Subscriptions managed through Google Play or the App Store can be cancelled anytime from your device's subscription settings. Cancelling stops future charges; you keep PRO access until the end of your current billing period.",
  },
  {
    q: "What languages does SayItBetter support?",
    a: "Right now, English only. We're exploring multi-language support — PRO subscribers will get access to new languages as they launch, at no extra cost.",
  },
  {
    q: "Does SayItBetter give mental-health or legal advice?",
    a: "No. SayItBetter offers communication suggestions only. It is not a substitute for professional counseling, therapy, legal guidance, or crisis support. If you're in crisis or experiencing thoughts of self-harm, please contact a local crisis line (988 in the U.S.) or emergency services immediately.",
  },
  {
    q: "I forgot my password. How do I reset it?",
    a: "On the login screen tap \"Forgot password?\" and enter your email. We'll send you a reset link. If you don't receive it within a few minutes, check your spam folder or email us at " + SUPPORT_EMAIL + ".",
  },
  {
    q: "How do I delete my account?",
    a: "In the app: Settings → Delete account (at the bottom). You'll be asked to confirm with your password. Deletion is immediate and permanent — it removes your profile, history, and all data. You can also delete your account from sayitbetter.me/delete-account without logging in first.",
  },
  {
    q: "I have a question or feedback not covered here.",
    a: "We'd love to hear from you. Email us at " + SUPPORT_EMAIL + " and we'll get back to you as quickly as possible.",
  },
];

function FAQItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden"
      data-testid={`faq-item-${index}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`faq-toggle-${index}`}
        className="w-full flex items-start gap-3 px-4 py-3.5 text-left hover:bg-[#F7F9FB] transition-colors"
        aria-expanded={open}
      >
        <span className="flex-1 text-[14px] font-semibold text-[#1F3A5F] leading-snug pt-0.5">
          {item.q}
        </span>
        <ChevronDown
          size={18}
          className={`text-[#A0A7B4] flex-shrink-0 mt-0.5 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          className="px-4 pb-4 pt-1 text-[13px] text-[#6B7280] leading-relaxed"
          data-testid={`faq-answer-${index}`}
        >
          {item.a}
        </div>
      )}
    </div>
  );
}

export default function HelpFaqPage() {
  const [feedback, setFeedback] = useState("");

  const sendFeedback = () => {
    const trimmed = feedback.trim();
    if (!trimmed) return;
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "unknown";
    const body = `${trimmed}

—
App: SayItBetter v${APP_VERSION}
Device: ${ua}`;
    const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("SayItBetter Feedback")}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  };

  return (
    <div className="min-h-screen bg-[#F7F9FB]" data-testid="help-faq-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/app/settings" data-testid="faq-back" className="text-[#6B7280] hover:text-[#1F3A5F]">
            <ArrowLeft size={20} />
          </Link>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-[#1F3A5F]">Help &amp; FAQ</h1>
            <p className="text-[11px] text-[#A0A7B4]">Common questions about SayItBetter</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-2.5">
        {FAQS.map((item, i) => (
          <FAQItem key={i} item={item} index={i} />
        ))}

        {/* Send feedback card */}
        <div
          className="bg-white rounded-2xl border border-[#E5E7EB] p-4 mt-4"
          data-testid="faq-feedback-card"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-[#EAF6F4] flex items-center justify-center flex-shrink-0">
              <Send size={16} className="text-[#4FB3A5]" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-[#1F3A5F]">Send feedback</h3>
              <p className="text-[12px] text-[#6B7280] leading-snug">
                Found a bug or have a feature request? We read every message.
              </p>
            </div>
          </div>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Tell us what's on your mind…"
            rows={3}
            data-testid="faq-feedback-input"
            className="w-full text-[13px] text-[#1F3A5F] placeholder:text-[#A0A7B4] bg-[#F7F9FB] border border-[#E5E7EB] rounded-xl px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-[#4FB3A5]/40 focus:border-[#4FB3A5]"
          />
          <button
            onClick={sendFeedback}
            disabled={!feedback.trim()}
            data-testid="faq-feedback-send"
            className="mt-2.5 w-full inline-flex items-center justify-center gap-2 bg-[#1F3A5F] text-white font-semibold text-[13px] py-2.5 rounded-xl hover:bg-[#2a4a75] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#1F3A5F]"
          >
            <Send size={14} />
            Send feedback
          </button>
          <p className="text-[10px] text-[#A0A7B4] mt-2 leading-snug">
            Opens your email app. App version and device info are included automatically to help us debug.
          </p>
        </div>

        {/* Contact CTA */}
        <div
          className="bg-gradient-to-br from-[#1F3A5F] via-[#2a4a75] to-[#4FB3A5] rounded-2xl p-5 text-white mt-6"
          data-testid="faq-contact-card"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20 flex-shrink-0">
              <Mail size={18} />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-bold mb-0.5">Still have questions?</h3>
              <p className="text-sm text-white/85 leading-snug">
                We read every email. Reach out and we'll get back to you personally.
              </p>
            </div>
          </div>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=Question%20about%20SayItBetter`}
            data-testid="faq-contact-btn"
            className="inline-flex items-center justify-center w-full bg-white text-[#1F3A5F] font-bold py-2.5 rounded-xl hover:bg-white/90 active:scale-95 transition-all shadow-sm text-[13px]"
          >
            Email {SUPPORT_EMAIL}
          </a>
        </div>

        {/* Legal Links */}
        <div className="flex items-center justify-center gap-5 pt-6 pb-4 text-xs text-[#A0A7B4]">
          <Link to="/privacy" className="inline-flex items-center gap-1 hover:text-[#1F3A5F]" data-testid="faq-privacy-link">
            <Shield size={12} /> Privacy Policy
          </Link>
          <Link to="/terms" className="inline-flex items-center gap-1 hover:text-[#1F3A5F]" data-testid="faq-terms-link">
            <FileText size={12} /> Terms of Service
          </Link>
        </div>
      </div>
    </div>
  );
}
